import { useCallback, useRef } from 'react';
import { AxiosResponse } from 'axios';

import { initSubmission, initSubmissionPoll } from 'src/utils/services';

import {
  API_RESPONSE_STATUS,
  COSMOS_POLL_STATUS,
  DATA_INITIALISATION_STATUS,
  JOURNEY_TYPES,
} from 'core/constants';
import { initiateCosmosPolling } from 'core/helpers';

import { SubmissionParams } from 'core/types';
import PollClient from 'core/helpers/PollClient';
import { ValueOf } from 'src/utils/types';

export const useSubmissionInit = ({
  journeyType,
}: {
  journeyType: ValueOf<typeof JOURNEY_TYPES>;
}) => {
  const pollClientRef = useRef<PollClient<AxiosResponse> | null>(null);

  return useCallback(
    async ({
      dataType,
      submissionMode,
      payload,
      handleSuccess,
      handleFailure,
      handlePollResult,
      customPollingInterval,
    }: SubmissionParams) => {
      if (pollClientRef.current) {
        pollClientRef.current.abort();
        pollClientRef.current = null;
      }

      try {
        const res = await initSubmission(
          journeyType,
          dataType,
          submissionMode,
          payload
        );

        const { data } = res;
        const { statusCode, result, status, error } = data || {};

        const shouldFail =
          statusCode !== 200 ||
          status.toLocaleLowerCase() !==
            API_RESPONSE_STATUS.SUCCESS.toLocaleLowerCase() ||
          !result ||
          (!result.poll &&
            result.dataInitialisationStatus !==
              DATA_INITIALISATION_STATUS.SUCCESS) ||
          (result.poll && !result.pollConfig?.pollId);

        if (shouldFail) {
          handleFailure({ message: error?.message });
          return;
        }

        if (
          !result.poll &&
          result.dataInitialisationStatus === DATA_INITIALISATION_STATUS.SUCCESS
        ) {
          handleSuccess(result.data);
          return;
        }

        const { maxAttempts, pollInterval, pollId } = result.pollConfig;

        // Use custom polling interval if provided, otherwise use API response interval
        const effectivePollingInterval = customPollingInterval || pollInterval;

        pollClientRef.current = initiateCosmosPolling<AxiosResponse<any>>({
          pollingApiFn: () => initSubmissionPoll(journeyType, pollId),
          maxAttempts,
          pollInterval: effectivePollingInterval,
          continuePollingCondition: pollRes =>
            pollRes?.status === 200 &&
            pollRes?.data?.status.toLocaleLowerCase() ===
              API_RESPONSE_STATUS.SUCCESS.toLocaleLowerCase() &&
            pollRes?.data?.result?.pollStatus === COSMOS_POLL_STATUS.CONTINUE,
          onPollSuccess: pollRes => {
            if (handlePollResult) {
              handlePollResult(pollRes);
              return;
            }

            const { data } = pollRes || {};
            const { status, result } = data || {};

            if (
              status.toLocaleLowerCase() ===
                API_RESPONSE_STATUS.SUCCESS.toLocaleLowerCase() &&
              result?.pollStatus === COSMOS_POLL_STATUS.SUCCESS &&
              result?.data
            ) {
              handleSuccess(pollRes.data?.result?.data);
            } else {
              handleFailure({ message: pollRes?.data?.error?.message });
            }
          },
          onPollFailed: error => {
            handleFailure(error);
          },
          onPollExpire: () => {
            handleFailure({ message: 'Polling expired' });
          },
        });
      } catch (error: any) {
        handleFailure(error);
      }
    },
    [journeyType]
  );
};
